import * as THREE from "three/webgpu";
import { GameEngine } from "~/engine/instance";
import { VOXEL_EMPTY } from "~/terrain/types";

export class TerrainBackground {
  private backgroundMesh: THREE.Mesh | null = null;
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  private isEnabled = false; // TEMP: it's too ugly right now

  /**
   * Create or update the terrain-following background mesh
   */
  public update(): void {
    if (!this.isEnabled) return;

    const { voxelSize } = GameEngine.voxelTerrain;

    // Sample terrain height at regular intervals
    const sampleInterval = voxelSize * 4; // Sample every 4 voxels for good detail
    const samples = 128;

    const vertices: number[] = [];
    const indices: number[] = [];
    const colors: number[] = []; // Add vertex colors for gradient

    // Sample terrain heights across the world width
    const terrainHeights: number[] = [];
    for (let i = 0; i < samples; i++) {
      const worldX = i * sampleInterval;
      const surfaceHeight = this.sampleTerrainHeight(worldX);
      terrainHeights.push(surfaceHeight);
    }

    // Create vertices for the background mesh with gradient fade
    let vertexIndex = 0;
    const fadeHeight = voxelSize * 4; // Revert to reasonable fade height
    const earthColor = [0.41, 0.32, 0.25]; // #68523F in RGB

    for (let i = 0; i < samples; i++) {
      const x = i * sampleInterval;
      const surfaceY = terrainHeights[i]!;

      // Position the background to start just slightly above the terrain surface
      const surfaceStartY = surfaceY;

      // Create vertices from above surface down to bottom of world
      // 1. Fade zone (from just above surface down to surface)
      const gradientSteps = 4; // Revert to reasonable steps

      for (let step = 0; step <= gradientSteps; step++) {
        const t = step / gradientSteps;
        const y = surfaceStartY + fadeHeight - fadeHeight * t; // Start just above surface, fade down to surface

        // Calculate alpha - fade from transparent above surface to opaque at surface
        const alpha = Math.min(1, t); // Fade from 0.0 to 1.0 as we approach surface

        vertices.push(x, y, -100);
        colors.push(earthColor[0]!, earthColor[1]!, earthColor[2]!, alpha);
      }

      // 2. Solid underground area (from surface down to bottom)
      const solidSteps = 4; // A few steps for the solid underground area

      for (let step = 1; step <= solidSteps; step++) {
        const t = step / solidSteps;
        const y = surfaceStartY - surfaceStartY * t; // From surface down to y=0

        vertices.push(x, y, -100);
        colors.push(earthColor[0]!, earthColor[1]!, earthColor[2]!, 1.0); // Full opacity underground
      }

      // Create triangles between adjacent samples
      if (i < samples - 1) {
        const currentBase = vertexIndex;
        const totalVerticesPerSample = gradientSteps + 1 + solidSteps;
        const nextBase = vertexIndex + totalVerticesPerSample;

        // Connect all vertices (gradient + solid)
        for (let step = 0; step < totalVerticesPerSample - 1; step++) {
          const curr = currentBase + step;
          const next = nextBase + step;

          // Two triangles per step
          indices.push(curr, curr + 1, next);
          indices.push(curr + 1, next + 1, next);
        }
      }

      vertexIndex += gradientSteps + 1 + solidSteps;
    }

    // Create or update the background mesh
    if (this.backgroundMesh) {
      // Update existing geometry
      const geometry = this.backgroundMesh.geometry;
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3),
      );
      geometry.setAttribute(
        "color",
        new THREE.Float32BufferAttribute(colors, 4),
      );
      geometry.setIndex(indices);
      geometry.computeVertexNormals();
    } else {
      // Create new mesh
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3),
      );
      geometry.setAttribute(
        "color",
        new THREE.Float32BufferAttribute(colors, 4),
      );
      geometry.setIndex(indices);
      geometry.computeVertexNormals();

      const material = new THREE.MeshBasicMaterial({
        color: "#68523F", // Base underground earth color
        side: THREE.DoubleSide,
        vertexColors: true, // Enable vertex colors for gradient
        transparent: true, // Enable transparency for fade effect
        alphaTest: 0.01, // Lower threshold to render more transparent pixels
      });

      this.backgroundMesh = new THREE.Mesh(geometry, material);
      this.scene.add(this.backgroundMesh);
    }
  }

  /**
   * Sample terrain height at a specific world X coordinate
   */
  private sampleTerrainHeight(worldX: number): number {
    const { worldHeightInVoxels, voxelSize } = GameEngine.voxelTerrain;

    // Start from the top and work down to find the surface
    for (let voxelY = worldHeightInVoxels - 1; voxelY >= 0; voxelY--) {
      const voxel = GameEngine.voxelTerrain.getVoxelAt({
        x: Math.floor(worldX / voxelSize),
        y: voxelY,
      });

      if (voxel && voxel.terrainType !== VOXEL_EMPTY) {
        // Found surface - return the TOP of this voxel minus 6 voxels to fix positioning
        return voxelY * voxelSize + voxelSize - 6 * voxelSize;
      }
    }

    // No terrain found, return ground level
    return 0;
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    if (this.backgroundMesh) {
      this.scene.remove(this.backgroundMesh);

      // Dispose geometry and material
      this.backgroundMesh.geometry.dispose();
      if (Array.isArray(this.backgroundMesh.material)) {
        for (const material of this.backgroundMesh.material) {
          material.dispose();
        }
      } else {
        this.backgroundMesh.material.dispose();
      }

      this.backgroundMesh = null;
    }
  }
}
